import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import {
  buildCertificateUnitRows,
  formatCreditValue,
} from "./Utils/certificateUnitRowsUtils";

// Register Lexend font to match web view (Lexend is used in App.css: 'Lexend', 'Lora', 'sans-serif')
// Using jsDelivr CDN which serves Google Fonts in WOFF format - react-pdf supports TTF and WOFF
Font.register({
  family: "Lexend",
  src: "https://cdn.jsdelivr.net/npm/@fontsource/lexend-deca@5.2.10/files/lexend-deca-latin-400-normal.woff",
  fontWeight: "normal",
});
Font.register({
  family: "Lexend",
  src: "https://cdn.jsdelivr.net/npm/@fontsource/lexend-deca@5.2.10/files/lexend-deca-latin-500-normal.woff",
  fontWeight: 500,
});

// Register Google Sans Flex (math subset) for subscript characters like ₂
Font.register({
  family: "GoogleSansFlex",
  src: "https://cdn.jsdelivr.net/fontsource/fonts/google-sans-flex@latest/math-400-normal.woff",
  fontWeight: "normal",
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 57, // 20mm (DIN 5008) - space for title/logo
    paddingBottom: 85, // 30mm (DIN 5008) - space for signatures
    paddingLeft: 85, // 30mm (DIN 5008)
    paddingRight: 85, // 30mm (DIN 5008)
    fontSize: 10,
    fontFamily: "Lexend",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 24, // Increased space below logo
  },
  logo: {
    height: 36,
  },
  title: {
    fontSize: 20,
    color: "#35B28D",
    marginBottom: 10,
    fontFamily: "Lexend",
    fontWeight: 500,
  },
  sectionTitle: {
    fontSize: 14,
    color: "#35B28D",
    marginTop: 24,
    marginBottom: 8,
    fontFamily: "Lexend",
    fontWeight: 500,
    minPresenceAhead: 100, // Ensure section title doesn't break awkwardly - keep at least 100pt of content after it
  },
  text: {
    marginBottom: 4,
    fontSize: 10,
  },
  bold: {
    fontFamily: "Lexend",
    fontWeight: 500,
  },
  table: {
    display: "flex",
    flexDirection: "column",
    marginTop: 10,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontFamily: "Lexend",
    fontWeight: 500,
    paddingVertical: 8,
  },
  tableCell: {
    flex: 1.2,
    paddingHorizontal: 4,
    fontSize: 9,
    width: "30%",
  },
  tableCellSmall: {
    flex: 0.7,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "14%",
  },
  tableCellId: {
    flex: 0.8,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "16%",
  },
  tableCellAssetId: {
    flex: 1.2,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "24%",
  },
  subscript: {
    fontFamily: "GoogleSansFlex",
  },
  characteristicsCell: {
    flex: 1,
    paddingHorizontal: 4,
    fontSize: 8,
  },
  pageBreak: {
    marginTop: 20,
  },
  stampContainer: {
    marginTop: 24,
    alignSelf: "flex-end",
  },
  stampImage: {
    width: 90, // fixed width, auto height to preserve aspect ratio
    transform: "rotate(-15deg)",
  },
});

function formatDateRange(dates) {
  if (!dates || dates.length < 2) return "Not specified";

  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  // Handle MongoDB date format with $date property
  const getTimestamp = (date) => {
    if (!date) return null;
    return date?.$date ? date.$date : date;
  };

  const startTimestamp = getTimestamp(dates[0]);
  const endTimestamp = getTimestamp(dates[1]);

  if (!startTimestamp || !endTimestamp) return "Not specified";

  try {
    const startDate = new Date(startTimestamp).toLocaleDateString("en-US", options);
    const endDate = new Date(endTimestamp).toLocaleDateString("en-US", options);
    return `${startDate} - ${endDate}`;
  } catch (e) {
    return "Not specified";
  }
}

function formatDate(date) {
  if (!date) return "-";
  // Handle MongoDB date format with $date property
  const timestamp = date?.$date ? date.$date : date;
  if (!timestamp) return "-";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  try {
    return new Date(timestamp).toLocaleDateString("en-US", options);
  } catch (e) {
    return "-";
  }
}

function formatMethodology(methodologies) {
  if (Array.isArray(methodologies) && methodologies.length > 0) {
    return methodologies
      .map((method) => {
        if (typeof method === "object" && method.id && method.name) {
          return `- ${method.id}: ${method.name}`;
        }
        return `- ${method}`;
      })
      .join("\n");
  }
  if (typeof methodologies === "string") {
    return methodologies
      .split(", ")
      .map((method) => `- ${method}`)
      .join("\n");
  }
  return "-";
}

export const CertificatePDFDocument = ({
  yieldRecordCertificateDetails,
  finalTokenData,
  certificateUnitRows: certificateUnitRowsProp,
  assetBasePath,
  bundledCreditsTotal: bundledCreditsTotalProp,
}) => {
  const certificateData = yieldRecordCertificateDetails[0] || {};
  const logoSrc = assetBasePath ? `${assetBasePath}/logo512.png` : "/logo512.png";
  const stampSrc = assetBasePath ? `${assetBasePath}/stamp.png` : "/stamp.png";
  const certificateUnitRows =
    certificateUnitRowsProp !== undefined && certificateUnitRowsProp !== null
      ? certificateUnitRowsProp
      : buildCertificateUnitRows(certificateData, undefined);
  const bundledCredits =
    bundledCreditsTotalProp != null
      ? bundledCreditsTotalProp
      : certificateData?.amount_of_tradable_certificates;

  const flatAssetsRecords = [];
  function getUniqueYields(yields) {
    if (!Array.isArray(yields)) {
      return [];
    }
    let uniqueYields = [];
    let seenYields = new Set();
    for (let yieldData of yields) {
      let uniqueKey = `${yieldData["yield_period"]}-${yieldData["yield_storage_duration"]}-${yieldData["yield_co2_realized"]}`;
      if (!seenYields.has(uniqueKey)) {
        seenYields.add(uniqueKey);
        uniqueYields.push(yieldData);
      }
    }
    return uniqueYields;
  }

  yieldRecordCertificateDetails.forEach(({ assets }) => {
    if (Array.isArray(assets) && assets.length > 0) {
      assets.forEach(
        ({
          asset_name,
          asset_co2_emission_equivalent,
          asset_equivalent_calulation_method,
          yields,
          asset_parameters,
        }) => {
          const uniqueYieldsRecords = getUniqueYields(yields);

          uniqueYieldsRecords.forEach(
            ({ yield_period, yield_storage_duration, yield_co2_realized }) => {
              const flatRecord = {
                asset_name,
                asset_co2_emission_equivalent,
                asset_equivalent_calulation_method,
                yield_period,
                yield_storage_duration,
                yield_co2_realized,
                asset_parameters,
              };

              flatAssetsRecords.push(flatRecord);
            }
          );
        }
      );
    }
  });

  const uniqueAssetsRecords = Array.from(
    new Set(
      flatAssetsRecords
        .filter((record) => record?.yield_co2_realized != null)
        .map(JSON.stringify)
    )
  ).map((recordStr) => JSON.parse(recordStr));

  const renderWithCO2e = (text) => {
    if (!text) {
      return null;
    }

    const parts = text.split(/tCO2e|tCO₂e|tCO‚e/g);
    const matches = text.match(/tCO2e|tCO₂e|tCO‚e/g) || [];

    const children = [];

    parts.forEach((part, index) => {
      if (part) {
        children.push(part);
      }

      if (index < matches.length) {
        children.push(
          <Text key={`co2e-${index}`}>
            tCO
            <Text style={styles.subscript}>₂</Text>
            e
          </Text>
        );
      }
    });

    return children;
  };

  return (
    <Document>
      <Page
        size="A4"
        style={styles.page}
        wrap={true} // Enable page wrapping
      >
        <View style={styles.header}>
          <Image
            src={logoSrc}
            style={styles.logo}
          />
        </View>

        <Text style={styles.title}>
          Proba Emission Reduction Certificate
        </Text>
        {certificateData.ghg_impact_summary && (
          <Text style={[styles.text, { marginBottom: 10 }]}>
            {renderWithCO2e(certificateData.ghg_impact_summary)}
          </Text>
        )}

        <Text
          style={[styles.sectionTitle, { marginTop: 0 }]}
          minPresenceAhead={120}
        >
          Certificate Details
        </Text>
        <Text style={styles.text}>
          <Text style={styles.bold}>Certificate bundle ID: </Text>
          {finalTokenData?.finaltoken_id
            ? `PTCB-${finalTokenData.finaltoken_id}`
            : "DRY-RUN-XXXX"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Bundle issuance date: </Text>
          {formatDate(finalTokenData?.created || new Date())}
        </Text>

        {certificateData.cropLabel && (
          <Text style={styles.text}>
            <Text style={styles.bold}>Crop: </Text>
            {certificateData.cropLabel}
          </Text>
        )}

        {certificateData.crop_yield_emissions?.totalCropYield != null && (
          <Text style={styles.text}>
            <Text style={styles.bold}>Total crop yield: </Text>
            {Number(certificateData.crop_yield_emissions.totalCropYield).toFixed(0)} ton
          </Text>
        )}

        {bundledCredits != null && (
          <Text style={styles.text}>
            <Text style={styles.bold}>Bundled credits: </Text>
            {formatCreditValue(bundledCredits)}
          </Text>
        )}


        {certificateUnitRows.length > 0 && (
            <View style={{ marginTop: 8 }}>
              <Text style={[styles.text, styles.bold, { marginBottom: 4 }]}>
                Certified Emission Reduction Units:
              </Text>
              <Text style={[styles.text, { marginBottom: 8 }]}>
                Each unit represents a verified quantity of emission reductions and is issued as a unique digital token recorded on the blockchain.
              </Text>
              <View style={styles.table} minPresenceAhead={50}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.tableCellId}>
                    ID
                  </Text>
                  <Text style={styles.tableCellSmall}>Token Creation Date</Text>
                  <Text style={styles.tableCellAssetId}>Asset ID</Text>
                  <Text style={styles.tableCellSmall}>Period</Text>
                  <Text style={styles.tableCellSmall}>Product Volume</Text>
                  <Text style={styles.tableCellSmall}>Token GHG Mitigation</Text>
                  <Text style={styles.tableCellSmall}>Bundled Amount</Text>
                </View>
                {certificateUnitRows.map((row) => (
                  <View key={row.key} style={styles.tableRow}>
                    <Text style={styles.tableCellId}>
                      {row.tokenId}
                    </Text>
                    <Text style={styles.tableCellSmall}>
                      {row.issuanceDate}
                    </Text>
                    <Text style={styles.tableCellAssetId}>
                      {row.assetId}
                    </Text>
                    <Text style={styles.tableCellSmall}>
                      {row.period}
                    </Text>
                    <Text style={styles.tableCellSmall}>
                      {row.productVolume}
                    </Text>
                    <Text style={styles.tableCellSmall}>
                      {row.yield}
                    </Text>
                    <Text style={styles.tableCellSmall}>
                      {row.bundledAmount}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

        <Text style={styles.sectionTitle} minPresenceAhead={200}>Project Details</Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Project Name: </Text>
          {certificateData.intervention_name || "-"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Location: </Text>
          {certificateData.intervention_region || "-"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Project ID: </Text>
          {certificateData.intervention_project_id || "-"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>GHG Project Type: </Text>
          {certificateData.intervention_type === "removal"
            ? "Removal"
            : "Reduction"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Standards Used: </Text>
          {Array.isArray(certificateData.intervention_standards_used)
            ? certificateData.intervention_standards_used.join(", ")
            : certificateData.intervention_standards_used || "Proba Standard"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>GHG Project Summary: </Text>
          {certificateData.intervention_summary || "-"}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Crediting Period: </Text>
          {formatDateRange(certificateData.intervention_crediting_period)}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Project Period: </Text>
          {formatDateRange(certificateData.intervention_project_period)}
        </Text>

        <View style={{ marginTop: 12, padding: 10, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
          <Text style={{ fontSize: 11, fontFamily: "Lexend", fontWeight: 500, color: "#35B28D", marginBottom: 6 }}>
            Methodologies Applied:
          </Text>
          {Array.isArray(certificateData.intervention_method) &&
            certificateData.intervention_method.length > 0 ? (
            certificateData.intervention_method.map((method, idx) => (
              <Text key={idx} style={{ fontSize: 9, marginLeft: 8, marginBottom: 3 }}>
                • {method.id}: {method.name}
              </Text>
            ))
          ) : typeof certificateData.intervention_method === "string" ? (
            certificateData.intervention_method
              .split(", ")
              .map((method, idx) => (
                <Text key={idx} style={{ fontSize: 9, marginLeft: 8, marginBottom: 3 }}>
                  • {method}
                </Text>
              ))
          ) : (
            <Text style={{ fontSize: 9, marginLeft: 8 }}>-</Text>
          )}
        </View>

        {certificateData.emission_reduction_overview && (
          <View style={styles.pageBreak} wrap={false}>
            <Text style={styles.sectionTitle} minPresenceAhead={200}>
              Emission Reduction Overview
            </Text>
            <View style={styles.table} wrap={false} minPresenceAhead={50}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View style={styles.tableCellSmall}>
                  <Text>Baseline</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e
                  </Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Project</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e
                  </Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Reduction</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e
                  </Text>
                </View>
                <Text style={styles.tableCellSmall}>Reduction (%)</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.baseline != null
                    ? Number(
                      certificateData.emission_reduction_overview.baseline
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.project != null
                    ? Number(
                      certificateData.emission_reduction_overview.project
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.delta != null
                    ? Number(
                      certificateData.emission_reduction_overview.delta
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .reduction_percent != null
                    ? `${Number(
                      certificateData.emission_reduction_overview
                        .reduction_percent
                    ).toFixed(1)} %`
                    : "-"}
                </Text>
              </View>
            </View>

            <Text
              style={[styles.text, { marginTop: 8 }]}
              minPresenceAhead={60}
            >
              <Text style={styles.bold}>
                Emission Intensity
              </Text>
            </Text>
            <View style={styles.table} wrap={false} minPresenceAhead={50}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View style={styles.tableCellSmall}>
                  <Text>Baseline</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e / t crop
                  </Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Project</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e / t crop
                  </Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Reduction</Text>
                  <Text style={{ fontSize: 7 }}>
                    tCO
                    <Text style={styles.subscript}>₂</Text>
                    e / t crop
                  </Text>
                </View>
                <Text style={styles.tableCellSmall}>Reduction (%)</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .emission_intensity?.baseline != null
                    ? Number(
                      certificateData.emission_reduction_overview
                        .emission_intensity.baseline
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .emission_intensity?.project != null
                    ? Number(
                      certificateData.emission_reduction_overview
                        .emission_intensity.project
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .emission_intensity?.reduction != null
                    ? Number(
                      certificateData.emission_reduction_overview
                        .emission_intensity.reduction
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .emission_intensity?.baseline != null &&
                    certificateData.emission_reduction_overview
                      .emission_intensity?.reduction != null &&
                    certificateData.emission_reduction_overview.emission_intensity.baseline !== 0
                    ? `${Number(
                      (certificateData.emission_reduction_overview.emission_intensity.reduction /
                        certificateData.emission_reduction_overview.emission_intensity.baseline) *
                      100
                    ).toFixed(1)} %`
                    : "-"}
                </Text>
              </View>
            </View>
          </View>
        )}

        {certificateData.emission_breakdown && (
          <View style={styles.pageBreak}>
            <View wrap={false}>
              <Text style={styles.sectionTitle} minPresenceAhead={300}>
                Emission Breakdown
              </Text>
              <View wrap={false}>
                <Text
                  style={[styles.text, { marginTop: 8, marginBottom: 4 }]}
                  minPresenceAhead={60}
                >
                  <Text style={styles.bold}>By Fertilizer Emission Type</Text>
                </Text>
                <View style={styles.table} wrap={false} minPresenceAhead={50}>
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.tableCell}>Component</Text>
                    <View style={styles.tableCellSmall}>
                      <Text>Baseline</Text>
                      <Text style={{ fontSize: 7 }}>
                        tCO
                        <Text style={styles.subscript}>₂</Text>
                        e
                      </Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Project</Text>
                      <Text style={{ fontSize: 7 }}>
                        tCO
                        <Text style={styles.subscript}>₂</Text>
                        e
                      </Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Emission Reduction</Text>
                      <Text style={{ fontSize: 7 }}>
                        tCO
                        <Text style={styles.subscript}>₂</Text>
                        e
                      </Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Reduction Intensity</Text>
                      <Text style={{ fontSize: 7 }}>
                        tCO
                        <Text style={styles.subscript}>₂</Text>
                        e / t crop
                      </Text>
                    </View>
                  </View>
                  {certificateData.emission_breakdown.byEmissionType.map((row, idx) => (
                    <View key={idx} style={styles.tableRow}>
                      <Text
                        style={[
                          styles.tableCell,
                          row.component === "Total" ? styles.bold : {},
                        ]}
                      >
                        {row.component}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.baseline != null ? Number(row.baseline).toFixed(3) : "-"}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.project != null ? Number(row.project).toFixed(3) : "-"}
                      </Text>
                      <Text
                        style={[
                          styles.tableCellSmall,
                          row.component === "Total" ? styles.bold : {},
                        ]}
                      >
                        {row.delta != null ? Number(row.delta).toFixed(3) : "-"}
                      </Text>
                      <Text
                        style={[
                          styles.tableCellSmall,
                          row.component === "Total" ? styles.bold : {},
                        ]}
                      >
                        {row.deltaPerTon != null ? Number(row.deltaPerTon).toFixed(3) : "-"}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
            <View wrap={false}>
              <Text
                style={[styles.text, { marginTop: 8, marginBottom: 4 }]}
                minPresenceAhead={60}
              >
                <Text style={styles.bold}>By Input Type</Text>
              </Text>
              <View style={styles.table} wrap={false} minPresenceAhead={50}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                  <Text style={styles.tableCell}>Input Type</Text>
                  <View style={styles.tableCellSmall}>
                    <Text>Baseline</Text>
                    <Text style={{ fontSize: 7 }}>
                      tCO
                      <Text style={styles.subscript}>₂</Text>
                      e
                    </Text>
                  </View>
                  <View style={styles.tableCellSmall}>
                    <Text>Project</Text>
                    <Text style={{ fontSize: 7 }}>
                      tCO
                      <Text style={styles.subscript}>₂</Text>
                      e
                    </Text>
                  </View>
                  <View style={styles.tableCellSmall}>
                    <Text>Emission Reduction</Text>
                    <Text style={{ fontSize: 7 }}>
                      tCO
                      <Text style={styles.subscript}>₂</Text>
                      e
                    </Text>
                  </View>
                  <View style={styles.tableCellSmall}>
                    <Text>Reduction Intensity</Text>
                    <Text style={{ fontSize: 7 }}>
                      tCO
                      <Text style={styles.subscript}>₂</Text>
                      e / t crop
                    </Text>
                  </View>
                </View>
                {certificateData.emission_breakdown.byInputType.map((row, idx) => {
                  const isTotalRow = row.inputType === "Total";
                  const isFertilizerWithInhibitor =
                    row.inputType === "Fertilizers" && row.includesInhibitor;
                  const label = isFertilizerWithInhibitor
                    ? "Fertilizers (+ Inhibitor)"
                    : row.inputType;

                  return (
                    <View key={idx} style={styles.tableRow}>
                      <Text
                        style={[
                          styles.tableCell,
                          isTotalRow ? styles.bold : {},
                        ]}
                      >
                        {label}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.baseline != null ? Number(row.baseline).toFixed(3) : "-"}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.project != null ? Number(row.project).toFixed(3) : "-"}
                      </Text>
                      <Text
                        style={[
                          styles.tableCellSmall,
                          isTotalRow ? styles.bold : {},
                        ]}
                      >
                        {row.delta != null ? Number(row.delta).toFixed(3) : "-"}
                      </Text>
                      <Text
                        style={[
                          styles.tableCellSmall,
                          isTotalRow ? styles.bold : {},
                        ]}
                      >
                        {row.deltaPerTon != null ? Number(row.deltaPerTon).toFixed(3) : "-"}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        <View style={styles.pageBreak} wrap={false}>
          <Text style={styles.sectionTitle} minPresenceAhead={100}>Governance and Assurance</Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Project Developer: </Text>
            {certificateData.intervention_operator || "-"}
          </Text>
          {certificateData.intervention_project_sponsor && (
            <Text style={styles.text}>
              <Text style={styles.bold}>Project Sponsor: </Text>
              {certificateData.intervention_project_sponsor}
            </Text>
          )}
          <Text style={styles.text}>
            <Text style={styles.bold}>Validator: </Text>
            {certificateData.intervention_validator_of_quality || "-"}
          </Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Verifier: </Text>
            {certificateData.intervention_verifier_of_quality || "-"}
          </Text>
        </View>

        {/* Certificate stamp, placed after final section */}
        <View style={styles.stampContainer}>
          <Image src={stampSrc} style={styles.stampImage} />
        </View>
      </Page>
    </Document>
  );
};
